import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart,
  ArrowRight,
  Sparkles,
  Loader2,
  Lock,
  ScanFace,
} from "lucide-react";
import { toast } from "sonner";
import {
  foundingLockInRegisterAndCheckout,
  claimCampaignAndCheckout,
} from "../lib/api";
import { track } from "../lib/analytics";
import {
  trackViewContent,
  trackAddToCart,
  trackInitiateCheckout,
} from "../lib/marketingPixels";
import { captureUtmFromUrl } from "../lib/utm";
import { useAuth } from "../context/AuthContext";
import { startGoogleLogin } from "../lib/googleAuth";

/**
 * /pride — Pride 2026 paid-traffic landing page.
 *
 * Redesigned (Feb 2026) from a "$49.99/yr commit upfront" wall into a
 * lean info-first landing mirroring /start. TikTok ad data showed
 * 235 clicks → 0 signups on the old design — credit-card-upfront
 * is too aggressive for cold social-media traffic.
 *
 * New funnel:
 *   1. Cold visitor lands on /pride from TikTok ad
 *   2. Sees value pitch + single "Start free →" CTA above the fold
 *   3. Optionally scrolls to the "Already sure?" Pride lock-in form
 *      (kept for high-intent visitors who DO want to commit upfront)
 *   4. Free signup path tagged with `?campaign=pride_2026` so we know
 *      they came from Pride for retroactive upgrade offers later
 *
 * Mechanics for the direct-buy path (unchanged from prior version):
 *   • $49.99/yr Stripe subscription, grandfathered forever via Stripe
 *   • Backend tags user with `pride_2026_signup: true`
 *   • Two auth paths: Google OAuth (one-tap) + email/password
 */
const PRIDE_CAMPAIGN = "pride_2026";

const GoogleIcon = () => (
  <svg
    viewBox="0 0 48 48"
    className="h-5 w-5"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
    <path fill="none" d="M0 0h48v48H0z" />
  </svg>
);

const Pride = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [googleLoading, setGoogleLoading] = useState(false);

  // ── Direct-buy form state (below the fold, only used by visitors
  // who scroll AND click "Already sure?") ───────────────────────
  const [showDirectBuy, setShowDirectBuy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");

  // ── Mount: capture UTMs from TikTok / Google Ads / etc, fire
  // ViewContent for ad-platform optimization, log internal funnel. ──
  useEffect(() => {
    captureUtmFromUrl();
    track("pride_2026_viewed", { source: "/pride" });
    trackViewContent({
      content_type: "pride_landing",
      content_id: "pride_2026",
      value: 49.99,
      currency: "USD",
    });
  }, []);

  // ── Post-Google return handler ─────────────────────────────────
  // When the user comes back from Google auth (`/pride?google=1`),
  // they're now authenticated. Auto-trigger the campaign-claim +
  // Stripe checkout so the round-trip feels seamless.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("google") !== "1") return;
    if (!user) return; // wait for auth context to populate

    let cancelled = false;
    (async () => {
      setGoogleLoading(true);
      track("pride_2026_google_return_clicked");
      trackAddToCart({
        content_id: "pride_2026",
        content_name: "Pride 2026 · $49.99/yr forever",
        content_type: "subscription",
        value: 49.99,
        currency: "USD",
        quantity: 1,
      });
      try {
        const { url } = await claimCampaignAndCheckout({
          campaign: PRIDE_CAMPAIGN,
          origin_url: window.location.origin,
        });
        if (!cancelled) {
          trackInitiateCheckout({
            content_id: "pride_2026",
            value: 49.99,
            currency: "USD",
          });
          window.location.href = url;
        }
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        toast.error(
          typeof detail === "string"
            ? detail
            : "Couldn't start Pride checkout. Try again.",
        );
        setGoogleLoading(false);
        navigate("/pride", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, location.search, navigate]);

  // ── Primary CTA: free signup with Pride campaign tag ───────────
  // Cold-traffic best practice — let them see the app FIRST, then
  // offer Pride pricing as a perk after they've experienced value.
  const handleStartFree = () => {
    track("pride_2026_start_free_clicked");
    // Stamp the session as Pride-campaign-sourced even if the user
    // arrived organically (no utm_* params). This lets us surface
    // "claim Pride pricing" reminders in-app + tag the eventual
    // signup event with campaign attribution for /admin/analytics.
    try {
      const existing = JSON.parse(
        sessionStorage.getItem("trubeaute.utm.v1") || "{}",
      );
      if (!existing.utm_campaign) {
        sessionStorage.setItem(
          "trubeaute.utm.v1",
          JSON.stringify({
            ...existing,
            utm_campaign: existing.utm_campaign || "pride_2026",
            utm_source: existing.utm_source || "pride_landing",
            utm_medium: existing.utm_medium || "organic",
            _captured_at: new Date().toISOString(),
            _landing_path: "/pride",
          }),
        );
      }
    } catch {
      // sessionStorage disabled — fall through silently
    }
    navigate("/login?signup=1");
  };

  // ── Direct-buy path (below the fold, high-intent visitors) ─────
  // Google one-tap signup → returns to /pride?google=1 → auto-runs
  // claim-and-checkout in the useEffect above.
  const handleGoogleSignUp = () => {
    if (!agreed) {
      setFormError("Please agree to the Terms and Privacy Policy.");
      return;
    }
    setFormError("");
    setGoogleLoading(true);
    track("pride_2026_google_clicked");
    // White-labeled Google OAuth — comes back via /auth/google, then the
    // callback forwards to /pride?google=1 which auto-runs claim+checkout.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    startGoogleLogin("/pride?google=1");
  };

  // Email/password direct-buy → Stripe checkout in one shot.
  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setFormError("");
    if (!email || !password) {
      setFormError("Email and password are both required.");
      return;
    }
    if (password.length < 8) {
      setFormError("Password should be at least 8 characters.");
      return;
    }
    if (!agreed) {
      setFormError("Please agree to the Terms and Privacy Policy.");
      return;
    }
    setLoading(true);
    track("pride_2026_register_clicked");
    trackAddToCart({
      content_id: "pride_2026",
      content_name: "Pride 2026 · $49.99/yr forever",
      content_type: "subscription",
      value: 49.99,
      currency: "USD",
      quantity: 1,
    });
    try {
      const { url } = await foundingLockInRegisterAndCheckout({
        email,
        password,
        name: name || null,
        origin_url: window.location.origin,
        locale: "en",
        tos_version: "2026-05-01",
        campaign: PRIDE_CAMPAIGN,
      });
      trackInitiateCheckout({
        content_id: "pride_2026",
        value: 49.99,
        currency: "USD",
      });
      window.location.href = url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
        setFormError(detail);
      } else {
        toast.error("Something went wrong. Try again in a sec.");
      }
      setLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen overflow-hidden"
      data-testid="pride-landing"
    >
      {/* ── Trans-flag-inspired background ─────────────────────── */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#A1D5F5] via-[#FBE7EE] to-white" />
      <div className="absolute inset-0 -z-10 opacity-60 bg-[radial-gradient(ellipse_at_top_right,_#F5B6CC,_transparent_55%)]" />
      <div className="absolute inset-0 -z-10 opacity-40 bg-[radial-gradient(ellipse_at_bottom_left,_#F2D3CE,_transparent_55%)]" />

      {/* ── Minimal top brand-mark only, no nav ─────────────────── */}
      <div className="relative z-10 px-6 sm:px-10 pt-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 sm:gap-3 text-[#5D4050] font-serif italic text-3xl sm:text-4xl"
          data-testid="pride-home-link"
        >
          TruBeauté <span className="text-2xl sm:text-3xl">🏳️‍⚧️</span>
        </Link>
      </div>

      {/* ── Hero — lean info-first, mirrors /start structure ────── */}
      <section className="relative z-10 px-6 sm:px-10 pt-10 sm:pt-16 pb-16">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
          >
            <p
              className="text-xs sm:text-sm font-medium uppercase tracking-[0.22em] text-[#9B6A6C] mb-5"
              data-testid="pride-kicker"
            >
              Pride 2026 · gender-expansive
            </p>

            {/* Same headline framing as /start so the cold visitor
                experiences brand consistency between marketing channels. */}
            <h1
              className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#2C2123] leading-[1.02] tracking-tight mb-6"
              data-testid="pride-hero-headline"
            >
              Beauty designed{" "}
              <span className="italic text-[#9B6A6C]">with you</span> in mind.
            </h1>

            <p className="text-lg sm:text-xl text-[#3C2F33] leading-relaxed mb-3 max-w-xl">
              Real-time AR makeup coaching from Trouvé — your AI digital
              drag mom. Personalized to your face, your goal, your vibe.
            </p>

            {/* Pride promo callout — present but soft, doesn't gate
                the experience. Below the value-pitch line, not above. */}
            <p
              className="text-sm sm:text-base text-[#5D4050] mb-9 max-w-xl inline-flex items-center gap-2"
              data-testid="pride-promo-callout"
            >
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#F2D3CE]">
                <Heart className="h-3.5 w-3.5 text-[#9B6A6C] fill-[#9B6A6C]" />
              </span>
              <span>
                <strong>Pride 2026 perk:</strong> sign up free, then claim{" "}
                <strong>$49.99/yr for life</strong> anytime.
              </span>
            </p>

            {/* THE primary CTA — single huge "Start free" button. */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-10">
              <button
                type="button"
                onClick={handleStartFree}
                className="group inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-9 py-4 text-base font-medium transition-all shadow-[0_8px_22px_rgba(155,106,108,0.40)] hover:-translate-y-0.5"
                data-testid="pride-start-free-cta"
              >
                Start free
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <p className="text-xs text-[#5C4B51] tracking-wider uppercase">
                No card · 3 free tutorials
              </p>
            </div>

            {/* Three trust bullets — same shape as /start so the
                cross-channel brand experience feels coherent. */}
            <ul
              className="grid sm:grid-cols-3 gap-5 max-w-2xl"
              data-testid="pride-trust-bullets"
            >
              <li className="flex items-start gap-2.5">
                <ScanFace className="h-4 w-4 text-[#9B6A6C] mt-0.5 shrink-0" />
                <span className="text-sm text-[#5C4B51] leading-snug">
                  AR mirror coaching, technique-by-technique
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <Heart className="h-4 w-4 text-[#9B6A6C] mt-0.5 shrink-0" />
                <span className="text-sm text-[#5C4B51] leading-snug">
                  Affirming routines, never &ldquo;fix what&rsquo;s wrong&rdquo;
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <Lock className="h-4 w-4 text-[#9B6A6C] mt-0.5 shrink-0" />
                <span className="text-sm text-[#5C4B51] leading-snug">
                  Encrypted at rest. Stealth Mode anytime.
                </span>
              </li>
            </ul>
          </motion.div>

          {/* Founder portrait — same as /start (consistency) but with
              the Pride pastel halo behind it so it sits in the page. */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.65, delay: 0.1, ease: "easeOut" }}
            className="relative aspect-[4/5] sm:aspect-[3/4] rounded-3xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(155,106,108,0.45)] border border-white/50"
          >
            <img
              src="/img/iyal-hero-v8-airbrush-tight.jpg"
              alt="Iyal Basen, founder of TruBeauté — the AI-powered makeup app designed for nonbinary and gender-expansive people. Pride 2026."
              className="w-full h-full object-cover object-top"
              loading="eager"
              fetchpriority="high"
            />
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(155,106,108,0.18) 100%)",
              }}
            />
          </motion.div>
        </div>
      </section>

      {/* ── Below the fold: "Already sure?" direct-buy path ───────
          Kept for high-intent visitors who already trust the brand
          and want to lock in Pride pricing immediately. Collapsed by
          default so it doesn't add visual friction to the primary
          free-signup flow above. ─────────────────────────────── */}
      <section className="relative z-10 px-6 sm:px-10 pb-20">
        <div className="max-w-2xl mx-auto">
          {!showDirectBuy ? (
            <button
              type="button"
              onClick={() => {
                track("pride_2026_direct_buy_expanded");
                setShowDirectBuy(true);
              }}
              className="block mx-auto text-sm text-[#5D4050] hover:text-[#9B6A6C] underline-offset-4 hover:underline transition-colors"
              data-testid="pride-direct-buy-toggle"
            >
              Already sure? <strong>Lock in Pride pricing now</strong> — $49.99/yr forever →
            </button>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="bg-white/85 backdrop-blur-sm border border-[#EADDD7] rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_rgba(155,106,108,0.15)]"
              data-testid="pride-direct-buy-form"
            >
              <div className="flex items-baseline justify-between mb-1">
                <h2 className="font-serif italic text-2xl text-[#2C2123]">
                  Claim Pride pricing
                </h2>
                <button
                  type="button"
                  onClick={() => setShowDirectBuy(false)}
                  className="text-xs text-[#8A7D82] hover:text-[#5C4B51]"
                  data-testid="pride-direct-buy-collapse"
                >
                  Close
                </button>
              </div>
              <p className="text-sm text-[#5C4B51] mb-5">
                <strong className="text-[#2C2123]">$49.99/year forever</strong>{" "}
                — locked in via Stripe. Cancel anytime.
              </p>

              <label className="flex items-start gap-2.5 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  disabled={loading || googleLoading}
                  className="mt-0.5 h-4 w-4 rounded border-[#EADDD7] text-[#9B6A6C] focus:ring-[#9B6A6C]"
                  data-testid="pride-agree-checkbox"
                />
                <span className="text-[12px] text-[#5C4B51] leading-snug">
                  I'm 13+ and agree to the{" "}
                  <Link
                    to="/terms"
                    target="_blank"
                    className="text-[#9B6A6C] underline-offset-2 hover:underline"
                  >
                    Terms
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/privacy"
                    target="_blank"
                    className="text-[#9B6A6C] underline-offset-2 hover:underline"
                  >
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>

              <button
                type="button"
                onClick={handleGoogleSignUp}
                disabled={loading || googleLoading}
                className="w-full bg-white hover:bg-[#FDFBF7] disabled:opacity-60 border border-[#E5D5D2] text-[#2C2123] px-6 py-3 rounded-full font-medium text-sm transition-colors inline-flex items-center justify-center gap-2.5 shadow-sm mb-4"
                data-testid="pride-google-btn"
              >
                {googleLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {user ? "Starting Pride checkout…" : "Redirecting…"}
                  </>
                ) : (
                  <>
                    <GoogleIcon />
                    Continue with Google
                  </>
                )}
              </button>

              <div className="flex items-center gap-3 mb-4 text-[10px] uppercase tracking-widest text-[#8A7D82]">
                <span className="h-px bg-[#EADDD7] flex-1" />
                Or with email
                <span className="h-px bg-[#EADDD7] flex-1" />
              </div>

              <form onSubmit={onSubmit} className="space-y-3">
                <input
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading || googleLoading}
                  className="w-full px-4 py-3 rounded-xl border border-[#EADDD7] bg-white text-[#2C2123] placeholder-[#8A7D82] focus:outline-none focus:ring-2 focus:ring-[#9B6A6C]/40 disabled:opacity-60"
                  data-testid="pride-email-input"
                />
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  placeholder="Password (8+ chars)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading || googleLoading}
                  className="w-full px-4 py-3 rounded-xl border border-[#EADDD7] bg-white text-[#2C2123] placeholder-[#8A7D82] focus:outline-none focus:ring-2 focus:ring-[#9B6A6C]/40 disabled:opacity-60"
                  data-testid="pride-password-input"
                />
                <input
                  type="text"
                  placeholder="First name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading || googleLoading}
                  className="w-full px-4 py-3 rounded-xl border border-[#EADDD7] bg-white text-[#2C2123] placeholder-[#8A7D82] focus:outline-none focus:ring-2 focus:ring-[#9B6A6C]/40 disabled:opacity-60"
                  data-testid="pride-name-input"
                />
                {formError && (
                  <p
                    className="text-sm text-[#9B6A6C]"
                    data-testid="pride-form-error"
                  >
                    {formError}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={loading || googleLoading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-6 py-3.5 font-medium text-sm transition-all shadow-[0_6px_18px_rgba(155,106,108,0.35)] disabled:opacity-60"
                  data-testid="pride-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Starting Stripe…
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Lock in Pride pricing — $49.99/yr
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          )}
        </div>
      </section>

      {/* ── Minimal footer — just legal links to keep funnel tight. */}
      <footer className="relative z-10 px-6 sm:px-10 pb-10">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[#8A7D82]">
          <span>© TruBeauté</span>
          <Link
            to="/privacy"
            className="hover:text-[#5C4B51] transition-colors"
            data-testid="pride-footer-privacy"
          >
            Privacy
          </Link>
          <Link
            to="/terms"
            className="hover:text-[#5C4B51] transition-colors"
            data-testid="pride-footer-terms"
          >
            Terms
          </Link>
          <Link
            to="/"
            className="hover:text-[#5C4B51] transition-colors"
            data-testid="pride-footer-home"
          >
            About TruBeauté →
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default Pride;
